import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'react-toastify';
import { useUploadCompanyDocument } from '@area-restrita/api-client/endpoints/company-document/company-document.gen';

import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Form, FormField } from '@/components/ui/form';
import InlineInput from '@/components/InlineInput';
import DocumentCategorySelector from '@/components/QuerySelectors/DocumentCategorySelector';
import FileInput from '@/components/FileInput';
import { useDialog } from '@/components/template/Dialog';

import { ICompanyDocument, companyDocumentSchema } from './schema';

const CreateCompanyDocument: React.FC = () => {
  const { handleClose } = useDialog();
  const navigate = useNavigate();

  const form = useForm<ICompanyDocument>({
    resolver: zodResolver(companyDocumentSchema),
  });

  const { mutateAsync } = useUploadCompanyDocument({
    mutation: {
      onSuccess: async ({ publicId }) => {
        toast.success('Documento cadastrado com sucesso!');

        handleClose();

        await navigate({
          to: '/documents/$companyDocumentPublicId',
          params: { companyDocumentPublicId: publicId },
        });
      },
      onError: () => toast.error('Erro ao cadastrar documento!'),
    },
  });

  const onSubmit = useCallback(
    (data: ICompanyDocument) =>
      mutateAsync({
        data: {
          name: data.name,
          documentCategoryId: Number(data.documentCategoryId.value),
          file: data.file,
        },
      }),
    [mutateAsync],
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-4 py-4">
          <FormField
            control={form.control}
            name="file"
            render={({ field }) => <FileInput {...field} />}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <InlineInput
                title="Nome"
                placeholder="Nome do documento"
                disabled={form.formState.isSubmitting}
                {...field}
              />
            )}
          />
          <FormField
            control={form.control}
            name="documentCategoryId"
            render={({ field }) => (
              <InlineInput
                title="Categoria"
                as={DocumentCategorySelector}
                disabled={form.formState.isSubmitting}
                {...field}
              />
            )}
          />
        </div>
        <DialogFooter>
          <Button type="submit" loading={form.formState.isSubmitting}>
            Cadastrar
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export default CreateCompanyDocument;
